import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  WritableSignal,
  signal,
  Signal,
  computed,
  ChangeDetectorRef
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { UntilDestroy } from '@ngneat/until-destroy';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription, combineLatest, merge, of, throwError } from 'rxjs';
import { catchError, debounceTime, filter, finalize, map, switchMap, tap } from 'rxjs/operators';

import { ArrayHelper, MarkFormAsTouchedIfInvalid } from 'tiime-expert-utils';
import {
  DialogBase,
  TiimeAlertModule,
  TiimeButtonModule,
  TiimeDialogModule,
  TiimeIconModule,
  TiimeLoaderModule,
  TiimeSnackbarService,
  TiimeTabsModule,
  TiimeTooltipModule
} from 'tiime-material';

import { BusinessUserApiService } from '@api-services/business-user-api.service';
import { CommonDataApiService } from '@api-services/common-data-api.service';
import { UserApiService } from '@api-services/user-api.service';
import { PreventMultipleClick } from '@decorators/prevent-multiple-click';
import { GICachedDataApiService } from '@gi/api/services/gi-cached-data-api.service';
import { ProspectAcquisitionChannel } from '@gi/enums/prospect-acquisition-channel.enum';
import { TiimeBusinessProAccountIntention } from '@gi/enums/tiime-business-pro-account-intention.enum';
import { ProspectForm } from '@gi/forms/prospect-form';
import { Contract } from '@gi/models/contract';
import { Prospect } from '@gi/models/prospect';
import { remainSubscribedOnError } from '@helpers/rxjs.helper';
import { AppStoreState } from '@interfaces/app-store-state';
import { BusinessUnit } from '@models/business-unit';
import { Civility } from '@models/civility';
import { CodeAndLabel } from '@models/code-and-label';
import { Company } from '@models/company';
import { Partner } from '@models/partner';
import { SearchUser } from '@models/search-user';
import { ChangeContractStatusType, ContractFacade } from '@modules/core/facades/contract.facade';
import { ProspectFacade } from '@modules/core/facades/prospect.facade';
import { NavigationService } from '@modules/core/navigation/navigation.service';
import { businessUserPartnerSelector } from '@modules/core/store/business-user/business-user-selector';
import { ContractsContainerComponent } from '@shared-components/contracts-container/contracts-container.component';
import { DuplicatedProspectsContainerComponent } from '@shared-components/duplicated-prospects-container/duplicated-prospects-container.component';
import { DuplicatedUsersContainerComponent } from '@shared-components/duplicated-users-container/duplicated-users-container.component';
import { ConfirmDialogData } from '@shared-dialogs/confirm-dialog/confirm-dialog-data';
import { ConfirmDialogService } from '@shared-dialogs/confirm-dialog/confirm-dialog.service';
import { ReturnedUpdateContractStatusDialogData } from '@shared-dialogs/update-contract-status-dialog/returned-update-contract-status-dialog-data';
import { ProspectFormComponent } from '@shared-forms/prospect-form/prospect-form.component';
import { AtByPipe } from '@shared-pipes/at-by/at-by.pipe';
import { CanCreateProspectCompanyPipe } from '@shared-pipes/can-create-prospect-company/can-create-prospect-company.pipe';
import { CreateProspectCompanyTooltipPipe } from '@shared-pipes/create-prospect-company-tooltip/create-prospect-company-tooltip.pipe';
import { ProspectCreatedByPipe } from '@shared-pipes/prospect-created-by/prospect-created-by.pipe';

import { CreateOrUpdateProspectDialogData } from './create-or-update-prospect-dialog-data';
import { ProspectCompanyContainerComponent } from './prospect-company-container/prospect-company-container.component';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-create-or-update-prospect-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TiimeDialogModule,
    TiimeButtonModule,
    TiimeIconModule,
    TiimeLoaderModule,
    TiimeTooltipModule,
    TiimeAlertModule,
    TiimeTabsModule,
    ProspectFormComponent,
    DuplicatedUsersContainerComponent,
    DuplicatedProspectsContainerComponent,
    AtByPipe,
    ProspectCreatedByPipe,
    ProspectCompanyContainerComponent,
    ContractsContainerComponent,
    CanCreateProspectCompanyPipe,
    CreateProspectCompanyTooltipPipe
  ],
  templateUrl: './create-or-update-prospect-dialog.component.html',
  styleUrls: ['./create-or-update-prospect-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateOrUpdateProspectDialogComponent extends DialogBase<CreateOrUpdateProspectDialogComponent> {
  readonly prospectForm = new ProspectForm();

  readonly civilities$: Observable<Civility[]> = this.commonDataApiService.getCivilities();
  readonly legalForms$: Observable<string[]> = this.commonDataApiService.getLegalForms();
  readonly businessUnits$: Observable<BusinessUnit[]> =
    this.businessUserApiService.getBusinessUserPartnerBusinessUnits();
  readonly partner$: Observable<Partner> = this.store.pipe(select(businessUserPartnerSelector));

  readonly duplicatedLoading: WritableSignal<boolean> = signal<boolean>(false);
  readonly displayDuplicated: WritableSignal<boolean> = signal<boolean>(false);
  readonly duplicatedUsers: WritableSignal<SearchUser[]> = signal<SearchUser[]>([]);
  readonly duplicatedProspects: WritableSignal<Prospect[]> = signal<Prospect[]>([]);
  readonly duplicatedLength: Signal<number> = computed(
    () => this.duplicatedUsers().length + this.duplicatedProspects().length
  );
  readonly duplicatedWording: Signal<string> = computed(() => {
    const contactWording = `${this.duplicatedUsers().length ? `${this.duplicatedUsers().length} contact${this.duplicatedUsers().length > 1 ? 's' : ''}` : ''}`;
    const prospectWording = ` ${this.duplicatedProspects().length ? `${this.duplicatedProspects().length} prospect${this.duplicatedProspects().length > 1 ? 's' : ''}` : ''}`;

    switch (this.duplicatedLength()) {
      case 0:
        return 'Aucun contact ni prospect correspondant';
      case 1:
        return `Correspondance :
        ${contactWording} 
        ${prospectWording}`;
      default:
        return `Correspondances :
        ${contactWording} 
        ${this.duplicatedUsers().length && this.duplicatedProspects().length ? `et` : ''}
        ${prospectWording}`;
    }
  });
  readonly displayNavigateToCompany: WritableSignal<boolean> = signal<boolean>(false);
  readonly acquisitionChannels: Signal<CodeAndLabel<ProspectAcquisitionChannel>[]> = toSignal(
    this.gICachedDataApiService.getProspectAcquisitionChannels()
  );
  readonly tiimeBusinessProAccountIntentions: Signal<CodeAndLabel<TiimeBusinessProAccountIntention>[]> = toSignal(
    this.gICachedDataApiService.getTiimeBusinessProAccountIntentions()
  );

  private shouldForceCloseWithProspect: boolean;
  private readonly subscription = new Subscription();

  constructor(
    dialogRef: MatDialogRef<CreateOrUpdateProspectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: CreateOrUpdateProspectDialogData,
    private readonly snackbarService: TiimeSnackbarService,
    private readonly commonDataApiService: CommonDataApiService,
    private readonly gICachedDataApiService: GICachedDataApiService,

    private readonly businessUserApiService: BusinessUserApiService,
    private readonly userApiService: UserApiService,
    private readonly navigationService: NavigationService,
    private readonly store: Store<AppStoreState>,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly contractFacade: ContractFacade,
    private readonly cdr: ChangeDetectorRef,
    private readonly prospectFacade: ProspectFacade
  ) {
    super(dialogRef);

    this.observeDuplicated();
    this.initProspectForm(data.prospect);
    this.displayNavigateToCompany.set(data.displayNavigateToCompany);
  }

  override cancel(): void {
    if (this.shouldForceCloseWithProspect && this.prospectForm.id.value) {
      this.prospectFacade
        .getProspect(this.prospectForm.id.value)
        .pipe(tap((prospect: Prospect) => super.close(prospect)))
        .subscribe();
    } else {
      super.cancel();
    }
  }

  @PreventMultipleClick()
  @MarkFormAsTouchedIfInvalid('prospectForm')
  submitProspectForm(): void {
    this.isLoading.set(true);
    this.prospectForm.disable();

    const prospect = this.prospectForm.toProspect();
    const obs = prospect.id ? this.updateProspect(prospect) : this.createProspect(prospect);

    obs
      .pipe(
        tap((createdOrUpdatedProspect: Prospect) => this.close(createdOrUpdatedProspect)),
        catchError((err: Error) => {
          this.isLoading.set(false);
          this.prospectForm.enable();
          return throwError(() => err);
        })
      )
      .subscribe();
  }

  setDisplayDuplicated(value: boolean): void {
    this.displayDuplicated.set(value);
  }

  refreshSearchDuplicated(): void {
    this.prospectForm.email.updateValueAndValidity();
  }

  navigateToCompanyOpportunitiesInNewTab(companyId: number): void {
    this.navigationService.navigateToCompanyContractsInNewTab(companyId);
  }

  createCompany(): void {
    const prospect = this.prospectForm.toProspect();
    const createCompanyObs = this.prospectFacade.createCompany(prospect);

    this.createOrUpdateContract(
      prospect,
      createCompanyObs,
      `Veuillez enregistrer vos modifications afin de pouvoir ajouter un dossier`
    )
      .pipe(
        filter((createdCompany: Company) => !!createdCompany),
        tap((createdCompany: Company) => {
          this.prospectForm.company.setValue(createdCompany);
          this.prospectForm.disable();
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  createContract(): void {
    const prospect = this.prospectForm.toProspect();
    const contractDialogObs = this.contractFacade.openCreateContractDialog(prospect);

    this.createOrUpdateContract(
      prospect,
      contractDialogObs,
      `Veuillez enregistrer vos modifications afin de pouvoir ajouter un contrat`
    )
      .pipe(
        filter((createdContract: Contract) => !!createdContract),
        tap((createdContract: Contract) =>
          this.prospectForm.contracts.setValue(ArrayHelper.addOne(this.prospectForm.contracts.value, createdContract))
        )
      )
      .subscribe();
  }

  updateContract(contract: Contract): void {
    const prospect = this.prospectForm.toProspect();
    const contractDialogObs = this.contractFacade.openUpdateContractDialog(contract.id);

    this.createOrUpdateContract(
      prospect,
      contractDialogObs,
      `Veuillez enregistrer vos modifications afin de pouvoir visualiser ce contrat`
    )
      .pipe(
        filter((updatedContract: Contract) => !!updatedContract),
        tap((updatedContract: Contract) => {
          this.prospectForm.contracts.setValue(
            ArrayHelper.updateOne(this.prospectForm.contracts.value, updatedContract)
          );
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  changeContractStatus(event: ChangeContractStatusType): void {
    if (event.contract.status === event.status) {
      return;
    }

    this.contractFacade
      .openConfirmUpdateContractStatus(event.contract.status, event.status, false)
      .pipe(
        filter((data: ReturnedUpdateContractStatusDialogData) => !!data),
        tap(() => this.isLoading.set(true)),
        switchMap((data: ReturnedUpdateContractStatusDialogData) =>
          this.contractFacade.updateContractStatus(event.contract.id, event.status, data.lossReason, data.lossComment)
        ),
        tap((updatedContract: Contract) => {
          this.shouldForceCloseWithProspect = true;
          this.prospectForm.contracts.setValue(
            ArrayHelper.updateOne(this.prospectForm.contracts.value, updatedContract)
          );
          this.snackbarService.openSuccess(`L'État du contrat a bien été modifié`);
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe();
  }

  private initProspectForm(prospect: Prospect): void {
    this.prospectForm.fromProspect(prospect);
    if (prospect.company) {
      this.prospectForm.disable();
    }
  }

  private observeDuplicated(): void {
    this.subscription.add(
      merge(this.prospectForm.email.valueChanges, this.prospectForm.phone.valueChanges)
        .pipe(
          filter(() => this.prospectForm.enabled),
          tap(() => {
            this.duplicatedLoading.set(true);
            this.duplicatedUsers.set([]);
            this.duplicatedProspects.set([]);
          }),
          debounceTime(500),
          filter(() => {
            const filter = this.prospectForm.email.valid || this.prospectForm.phone.valid;

            if (!filter) {
              this.duplicatedLoading.set(false);
            }

            return filter;
          }),
          switchMap(() => {
            const email = this.prospectForm.email.valid ? this.prospectForm.email.value : null;
            const phone = this.prospectForm.phone.valid ? this.prospectForm.phone.value : null;

            return combineLatest([this.getDuplicatedUsers(email, phone), this.getDuplicatedProspects(email, phone)]);
          }),
          tap(([users, prospects]: [SearchUser[], Prospect[]]) => {
            this.duplicatedLoading.set(false);
            this.duplicatedUsers.set(users);
            this.duplicatedProspects.set(prospects);
          }),
          catchError(error => {
            this.duplicatedLoading.set(false);

            return throwError(() => error);
          }),
          remainSubscribedOnError()
        )
        .subscribe()
    );
  }

  private createProspect(prospect: Prospect): Observable<Prospect> {
    return this.prospectFacade
      .createProspect(prospect)
      .pipe(tap(() => this.snackbarService.openSuccess('Le prospect a bien été ajouté')));
  }

  private updateProspect(prospect: Prospect): Observable<Prospect> {
    return this.prospectFacade
      .updateProspect(prospect)
      .pipe(tap(() => this.snackbarService.openSuccess('Le prospect a bien été modifié')));
  }

  private getDuplicatedUsers(email: string, phone: string): Observable<SearchUser[]> {
    return this.userApiService.searchUsersFromBusinessUserPartner({ email, phone });
  }

  private getDuplicatedProspects(email: string, phone: string): Observable<Prospect[]> {
    return this.prospectFacade
      .searchProspectsByEmailOrPhone(email, phone)
      .pipe(
        map((prospects: Prospect[]) =>
          prospects.filter((prospect: Prospect) => prospect.id !== this.prospectForm.id.value)
        )
      );
  }

  private createOrUpdateContract(
    prospect: Prospect,
    contractDialogObs: Observable<Contract>,
    confirmMessage: string
  ): Observable<Contract> {
    if (this.prospectForm.enabled && !this.prospectForm.valid) {
      this.prospectForm.markAllAsTouched();
      return of(null);
    }

    if (!this.prospectForm.dirty) {
      return contractDialogObs.pipe(
        filter((contract: Contract) => !!contract),
        tap(() => (this.shouldForceCloseWithProspect = true))
      );
    }

    return this.openConfirmUpdateProspect(confirmMessage).pipe(
      filter((confirm: boolean) => confirm),
      tap(() => {
        this.isLoading.set(true);
        this.prospectForm.disable();
      }),
      switchMap(() => this.updateProspect(prospect)),
      tap(() => {
        this.prospectForm.markAsPristine();
        this.shouldForceCloseWithProspect = true;
      }),
      finalize(() => {
        this.isLoading.set(false);
        this.prospectForm.enable();
      }),
      switchMap(() => contractDialogObs)
    );
  }

  private openConfirmUpdateProspect(confirmMessage: string): Observable<boolean> {
    return this.confirmDialogService
      .open({
        data: new ConfirmDialogData('Attention', confirmMessage, 'Enregistrer', 'Annuler', null, 'accent')
      })
      .afterClosed();
  }
}
